import React, { useCallback, useEffect, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import socket from 'services/socket';
import StaticAdsService from 'services/StaticAdsService';
import { ScrapeProgressEvent, ScrapeStage } from 'pages/VideoCreative/types';

import { StaticKeyword } from './types';

const STEPS = ['Launching browser', 'Loading Ad Library', 'Finding statics', 'Saving', 'Complete'];

/**
 * Fields the STATIC scrape emits that the shared ScrapeProgressEvent (written
 * for the video scrape) does not declare.
 *
 * `reportedResults` is Facebook's own header count for the search. It is the
 * only thing that separates "this keyword really has no ads" from "the fetch
 * could not read the page", so a zero-result run is unreadable without it.
 */
type StaticScrapeProgress = ScrapeProgressEvent & {
  reportedResults?: number;
  /** Rows collected but rejected by Snowflake — a loss, not a duplicate. */
  failed?: number;
  /** Rows saved whose image capture could not be queued — they land blank. */
  captureFailed?: number;
};

function stageToStep(stage: ScrapeStage | null): number {
  switch (stage) {
    case 'browserLaunching': return 0;
    case 'pageLoading': return 1;
    case 'collecting': return 2;
    case 'saving': return 3;
    case 'done': return 5; // past the last index = every step complete
    default: return -1;
  }
}

function toYMD(d: Date) { return d.toISOString().split('T')[0]; }
function daysAgo(n: number) { const d = new Date(); d.setDate(d.getDate() - n); return toYMD(d); }

const DATE_PRESETS = [
  { label: 'All time', from: '', to: '' },
  { label: 'Last month', from: daysAgo(30), to: toYMD(new Date()) },
  { label: 'Last 3 months', from: daysAgo(90), to: toYMD(new Date()) },
];

interface ImportStaticsDialogProps {
  service: StaticAdsService;
  open: boolean;
  onClose: () => void;
  onFinished: () => void;
  /**
   * Fetch this saved keyword instead of showing the form. Passing it starts the
   * fetch immediately and sends the keyword's ID — which is what makes its saved
   * advertiser filter apply. Mirrors ScrapeProgressDialog in Video Creative.
   */
  initialKeyword?: StaticKeyword | null;
}

/**
 * Fetch static ads for a keyword from the Facebook Ad Library.
 *
 * Deliberately does NOT preview the creatives as they arrive, unlike the video
 * fetch dialog: at collection time all we hold are Meta's own signed CDN links
 * (the capture worker mirrors them into our storage moments later), so a
 * preview grid here would be a wall of links that expire and may be
 * hotlink-blocked. Counts and advertiser names are honest about what is
 * actually known at this point; the images appear in the library once saved.
 */
export default function ImportStaticsDialog({ service, open, onClose, onFinished, initialKeyword }: ImportStaticsDialogProps) {
  const [keyword, setKeyword] = useState('');
  const [scrapeDepth, setScrapeDepth] = useState(20);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [scrapeRunId, setScrapeRunId] = useState<string | null>(null);
  const [stage, setStage] = useState<ScrapeStage | null>(null);
  const [searchUrl, setSearchUrl] = useState<string | null>(null);
  const [urlRewritten, setUrlRewritten] = useState(false);
  const [found, setFound] = useState(0);
  // What Facebook itself said the search holds, kept so a run that saved
  // nothing can say WHY.
  const [reportedResults, setReportedResults] = useState<number | null>(null);
  const [saved, setSaved] = useState<
    { inserted: number; skipped: number; restored: number; failed: number; captureFailed: number } | null
  >(null);
  const [advertisers, setAdvertisers] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  useEffect(() => {
    if (!scrapeRunId) return undefined;

    socket.emit('joinScrapeRoom', { scrapeRunId });
    const handler = (data: StaticScrapeProgress) => {
      if (data.scrapeRunId !== scrapeRunId) return;
      setStage(data.stage);
      if (data.searchUrl) {
        setSearchUrl(data.searchUrl);
        setUrlRewritten(!!data.urlRewritten);
      }
      if (data.stage === 'error') { setError(data.error || 'Fetch failed'); return; }
      if (typeof data.reportedResults === 'number') setReportedResults(data.reportedResults);
      if (typeof data.adsFound === 'number') setFound(data.adsFound);
      if (data.newAds?.length) {
        setAdvertisers(prev => Array.from(new Set([
          ...prev,
          ...data.newAds!.map(ad => (ad as { pageName?: string }).pageName || 'Unknown').filter(Boolean),
        ])));
      }
      if (data.stage === 'done') {
        setSaved({
          inserted: data.inserted ?? 0,
          skipped: data.skipped ?? 0,
          restored: data.refreshed ?? 0,
          failed: data.failed ?? 0,
          captureFailed: data.captureFailed ?? 0,
        });
        onFinished();
      }
    };

    socket.on('scrapeProgress', handler);
    return () => { socket.off('scrapeProgress', handler); };
  }, [scrapeRunId, onFinished]);

  const reset = () => {
    setScrapeRunId(null);
    setStage(null);
    setSearchUrl(null);
    setUrlRewritten(false);
    setFound(0);
    setReportedResults(null);
    setSaved(null);
    setAdvertisers([]);
    setError(null);
    setStarting(false);
  };

  const start = useCallback(async (kw?: StaticKeyword | null) => {
    setError(null);
    setStarting(true);
    try {
      // Saved keyword: send the ID and let the backend apply its depth, dates
      // and advertiser filter. Sending only the text would re-derive them and,
      // for the filter, previously dropped it entirely.
      const result = kw
        ? await service.triggerScrape({ keywordId: kw.keywordId })
        : await service.triggerScrape({
          keyword,
          scrapeDepth,
          startDateMin: dateFrom || null,
          startDateMax: dateTo || null,
        });
      setScrapeRunId(result.scrapeRunId);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Could not start the fetch');
    } finally {
      setStarting(false);
    }
  }, [service, keyword, scrapeDepth, dateFrom, dateTo]);

  // A preselected keyword skips the form entirely, the way the video fetch does.
  //
  // Guarded by a ref, not by scrapeRunId: setKeyword below re-creates `start`,
  // which re-runs this effect long before the trigger call returns an id — so
  // the id guard alone fired a SECOND Ad Library scrape (slow, rate-limited)
  // and then tracked only that one.
  const autoStartedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !initialKeyword) {
      autoStartedFor.current = null;
      return;
    }
    if (autoStartedFor.current === initialKeyword.keywordId) return;
    autoStartedFor.current = initialKeyword.keywordId;
    setKeyword(initialKeyword.keyword);
    void start(initialKeyword);
  }, [open, initialKeyword, start]);

  // With a keyword preselected there is nothing to fill in — go straight to progress.
  const showForm = !scrapeRunId && !initialKeyword;
  const running = !!scrapeRunId && stage !== 'done' && stage !== 'error';
  const activeStep = stageToStep(stage);
  // Inserted, skipped and restored are the only three ways a row lands. All
  // zero means the fetch produced nothing at all, whatever the stepper says.
  const nothingLanded = !!saved && !saved.inserted && !saved.skipped && !saved.restored;

  // ONE close path for the button, the backdrop and Esc — closing without the
  // reset left the previous run's results on screen at the next open. Closing
  // mid-run is allowed: the fetch is server-side and keeps going; its ads
  // appear in the library via Refresh.
  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{showForm ? 'Fetch static ads' : `Fetching: "${initialKeyword?.label || keyword}"`}</DialogTitle>
      <DialogContent dividers>
        {showForm ? (
          <Stack spacing={2}>
            <TextField
              size="small"
              label="Keyword"
              fullWidth
              autoFocus
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
            />
            <TextField
              size="small"
              label="Max ads"
              type="number"
              value={scrapeDepth}
              onChange={e => setScrapeDepth(Number(e.target.value) || 20)}
            />
            <Box>
              <Typography variant="caption" color="text.secondary">Date range</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 1 }}>
                {DATE_PRESETS.map(preset => (
                  <Chip
                    key={preset.label}
                    size="small"
                    label={preset.label}
                    color={dateFrom === preset.from && dateTo === preset.to ? 'primary' : 'default'}
                    onClick={() => { setDateFrom(preset.from); setDateTo(preset.to); }}
                  />
                ))}
              </Stack>
            </Box>
            <Alert severity="info">
              Fetches image and meme ads. Videos are excluded — those belong to Video Creative.
            </Alert>
          </Stack>
        ) : (
          <Box>
            <Stepper activeStep={activeStep} orientation="vertical">
              {STEPS.map(label => (
                <Step key={label}><StepLabel>{label}</StepLabel></Step>
              ))}
            </Stepper>

            {running && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">
                  {found ? `${found} ads found so far…` : 'Searching the Ad Library…'}
                </Typography>
              </Stack>
            )}

            {!!advertisers.length && (
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                {advertisers.slice(0, 12).map(name => (
                  <Chip key={name} size="small" variant="outlined" label={name} />
                ))}
              </Stack>
            )}

              {searchUrl && (
                <Alert
                  severity={urlRewritten ? 'warning' : 'info'}
                  sx={{ mt: 2, wordBreak: 'break-all' }}
                >
                  {urlRewritten
                    ? 'Facebook rewrote the search — this is what actually ran: '
                    : 'Searching the Ad Library with these filters: '}
                  <Link href={searchUrl} target="_blank" rel="noreferrer">open it</Link>
                </Alert>
              )}
            {saved && nothingLanded && (
              // A fetch that saved, skipped and restored NOTHING is not a
              // success — and the two possible causes need different actions,
              // so say which one this was rather than "Saved 0 new static ads".
              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Nothing was saved.</strong>{' '}
                {reportedResults === null
                  ? "Facebook's own result count couldn't be read, so it isn't clear whether this "
                    + 'keyword has no ads or the fetch failed to read the page. Open the search link '
                    + 'above to check it by hand.'
                  : reportedResults > 0
                    ? `Facebook reported ~${reportedResults} results for this search but none could be `
                      + 'read from the page — that points at the fetch, not at the keyword. Open the '
                      + 'search link above to confirm, then try again.'
                    : 'Facebook reported no results for this search either — this keyword has nothing '
                      + 'in the Ad Library for the dates chosen. Try a wider date range or a different '
                      + 'keyword.'}
                {saved.failed
                  ? ` ${saved.failed} row${saved.failed === 1 ? '' : 's'} reached us but failed to save.`
                  : ''}
              </Alert>
            )}
            {saved && !nothingLanded && (
              <Alert severity={saved.failed || saved.captureFailed ? 'warning' : 'success'} sx={{ mt: 2 }}>
                Saved {saved.inserted} new static ad{saved.inserted === 1 ? '' : 's'}
                {saved.restored ? `, restored ${saved.restored} previously removed` : ''}
                {saved.skipped ? `, skipped ${saved.skipped} already in the library` : ''}
                {saved.failed ? `, ${saved.failed} failed to save` : ''}.
                {saved.captureFailed
                  ? ` ${saved.captureFailed} ad${saved.captureFailed === 1 ? '' : 's'} could not be queued for `
                    + 'image capture and will show no image — re-fetch this keyword to retry them.'
                  : ''}
                {saved.inserted ? ' The images are being saved now and appear in the library shortly.' : ''}
                {' '}Nothing is analyzed automatically — open an ad and choose Analyze
                when you want its breakdown.
              </Alert>
            )}
            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>
          {running ? 'Close (fetch continues server-side)' : saved || error ? 'Close' : 'Cancel'}
        </Button>
        {/* Disabled while the trigger call is in flight: a second click queued a
            second Ad Library scrape and left the first one untrackable. */}
        {showForm && (
          <Button variant="contained" onClick={() => start()} disabled={!keyword.trim() || starting}>
            {starting ? 'Starting…' : 'Start fetch'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
